import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home, LogOut, Building2, PoundSterling, Wrench, FileText, Shield,
  Loader2, User, Calendar, CheckCircle, AlertTriangle, Clock, Eye,
  ThumbsUp, ThumbsDown, Phone, Mail, FileCheck, HardHat, Bell, Upload, Pen,
  CreditCard, XCircle, Key, Settings, ChevronDown, Lock, MessageSquare
} from "lucide-react";
import { API } from "@/App";
import FeedbackList from "@/components/feedback/FeedbackList";

const getPortalAuth = () => {
  const token = localStorage.getItem("portal_token");
  const client = JSON.parse(localStorage.getItem("portal_client") || "{}");
  return { token, client };
};

export const LandlordDashboard = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteModal, setQuoteModal] = useState(false);
  const [complianceModal, setComplianceModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { token, client } = getPortalAuth();

  // Document upload state
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadItem, setUploadItem] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({});

  // Password change state
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordStep, setPasswordStep] = useState("request"); // request, verify, change
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");

  useEffect(() => {
    if (!token || client.client_type !== "landlord") {
      navigate("/portal/login");
      return;
    }
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/portal/landlord/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.properties || []);
      setComplianceAlerts(response.data.compliance_alerts || []);
      setUploadedDocs(response.data.uploaded_documents || {});
      if (response.data.properties?.length > 0) {
        setSelectedProperty(response.data.properties[0]);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("portal_token");
        navigate("/portal/login");
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_client");
    navigate("/portal/login");
  };

  // Document upload handlers
  const openUploadModal = (itemKey, itemLabel) => {
    setUploadItem({ key: itemKey, label: itemLabel });
    setSelectedFile(null);
    setUploadModal(true);
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !uploadItem) {
      toast.error("Please select a file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', uploadItem.key);
      formData.append('landlord_id', client.landlord_id);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      
      await axios.post(
        `${API}/portal/landlord/upload-document`,
        formData,
        { headers }
      );
      
      toast.success(`${uploadItem.label} uploaded successfully!`);
      setUploadModal(false);
      setUploadItem(null);
      setSelectedFile(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  // Password change handlers
  const [deliveryMethod, setDeliveryMethod] = useState("");
  
  const resetPasswordModal = () => {
    setPasswordStep("request");
    setVerificationCode("");
    setVerificationToken("");
    setNewPassword("");
    setConfirmPassword("");
    setMaskedPhone("");
    setDeliveryMethod("");
  };

  const handleRequestPasswordChange = async () => {
    setPasswordLoading(true);
    try {
      const response = await axios.post(
        `${API}/portal/auth/request-password-change`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMaskedPhone(response.data.phone_masked || response.data.email_masked);
      setDeliveryMethod(response.data.delivery_method || "your phone/email");
      setPasswordStep("verify");
      toast.success(`Verification code sent via ${response.data.delivery_method}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification code");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setPasswordLoading(true);
    try {
      const response = await axios.post(
        `${API}/portal/auth/verify-code`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVerificationToken(response.data.verification_token);
      setPasswordStep("change");
      toast.success("Code verified! Now set your new password");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(
        `${API}/portal/auth/change-password`,
        { 
          verification_token: verificationToken,
          new_password: newPassword 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully!");
      setPasswordModal(false);
      resetPasswordModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatCurrency = (amount) => `£${parseFloat(amount || 0).toLocaleString()}`;

  const handleViewQuote = (job) => {
    setSelectedJob(job);
    setQuoteModal(true);
  };

  const handleQuoteAction = async (approved) => {
    if (!selectedJob) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/portal/landlord/quote/${selectedJob.job_id}/respond`,
        { approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approved ? "Quote approved! Work will proceed." : "Quote declined.");
      setQuoteModal(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process response");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle new payment route selection
  const handleQuotePaymentRoute = async (paymentRoute) => {
    if (!selectedJob) return;
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/portal/landlord/job/${selectedJob.job_id}/respond-to-quote`,
        { payment_route: paymentRoute },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (paymentRoute === "paying_instance" && response.data.payment_url) {
        // Redirect to Stripe payment
        toast.success("Redirecting to payment...");
        window.location.href = response.data.payment_url;
      } else if (paymentRoute === "deducting_off_next_months_rent") {
        toast.success(`Works authorised! £${response.data.total_amount?.toFixed(2)} will be deducted from your ${response.data.target_month} rent statement.`);
        setQuoteModal(false);
        fetchDashboard();
      } else if (paymentRoute === "rejected_landlord_sorting_own") {
        toast.success("Quote declined. You can arrange the repairs privately.");
        setQuoteModal(false);
        fetchDashboard();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process your choice");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplianceAction = async (alertId, paymentMethod) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/compliance-alerts/respond/${alertId}`,
        { payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (paymentMethod === "decline") {
        toast.success("Thank you for letting us know. Please send us the certificate once complete.");
      } else {
        toast.success("Order placed! The contractor will contact the tenant to arrange an appointment.");
      }
      setComplianceModal(false);
      setSelectedAlert(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process response");
    } finally {
      setActionLoading(false);
    }
  };

  const getComplianceStatus = (item) => {
    if (!item.expiry_date) return "unknown";
    const expiry = new Date(item.expiry_date);
    const now = new Date();
    const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return "expired";
    if (daysUntil < 30) return "expiring";
    return "valid";
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-800">Landlord Portal</h1>
                <p className="text-sm text-slate-500">Welcome, {client?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Settings</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    onClick={() => { resetPasswordModal(); setPasswordModal(true); }}
                    className="cursor-pointer"
                    data-testid="change-password-btn"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Password Change Modal */}
      <Dialog open={passwordModal} onOpenChange={(open) => { setPasswordModal(open); if (!open) resetPasswordModal(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-emerald-600" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              {passwordStep === "request" && "We'll send a verification code to your phone to confirm your identity."}
              {passwordStep === "verify" && `Enter the 6-digit code sent to ${maskedPhone}`}
              {passwordStep === "change" && "Enter your new password below."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {passwordStep === "request" && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <MessageSquare className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-600">
                  For your security, we need to verify your identity via SMS before changing your password.
                </p>
                <Button 
                  onClick={handleRequestPasswordChange} 
                  disabled={passwordLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="send-code-btn"
                >
                  {passwordLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Phone className="w-4 h-4 mr-2" /> Send Verification Code</>
                  )}
                </Button>
              </div>
            )}

            {passwordStep === "verify" && (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-slate-600 mb-4">
                    Enter the 6-digit code sent via <strong>{deliveryMethod}</strong>
                  </p>
                </div>
                <div>
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest font-mono"
                    data-testid="verification-code-input"
                  />
                </div>
                <Button 
                  onClick={handleVerifyCode} 
                  disabled={passwordLoading || verificationCode.length !== 6}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="verify-code-btn"
                >
                  {passwordLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Verify Code</>
                  )}
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={handleRequestPasswordChange}
                  disabled={passwordLoading}
                  className="w-full"
                >
                  Resend Code
                </Button>
              </div>
            )}

            {passwordStep === "change" && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-sm text-green-600 font-medium">Identity Verified!</p>
                </div>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button 
                  onClick={handleChangePassword} 
                  disabled={passwordLoading || newPassword.length < 6 || newPassword !== confirmPassword}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="change-password-submit-btn"
                >
                  {passwordLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                  ) : (
                    <><Key className="w-4 h-4 mr-2" /> Change Password</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Compliance Alerts Banner */}
        {complianceAlerts.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Bell className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-2">
                  Action Required: {complianceAlerts.length} Compliance Certificate{complianceAlerts.length > 1 ? 's' : ''} Expiring Soon
                </h3>
                <div className="space-y-2">
                  {complianceAlerts.map((alert) => (
                    <div key={alert.alert_id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-100">
                      <div>
                        <p className="font-medium text-slate-800">{alert.compliance_name}</p>
                        <p className="text-sm text-slate-500">{alert.property_address}</p>
                        <p className="text-xs text-amber-600 mt-1">Expires: {alert.expiry_date} ({alert.days_until_expiry} days)</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => { setSelectedAlert(alert); setComplianceModal(true); }}
                        data-testid={`action-alert-${alert.alert_id}`}
                      >
                        Take Action
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6">
          <Building2 className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-semibold text-slate-800">My Rental Properties</h2>
          {properties.some(p => p.active_maintenance > 0) && (
            <Badge className="bg-amber-500 text-white ml-2">Action Required</Badge>
          )}
        </div>

        {/* Properties Section */}
        {properties.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h2 className="text-xl font-semibold">No Rental Properties</h2>
                  <p className="text-slate-500 mt-2">You don&apos;t have any rental properties linked to your account.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Your Properties</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {properties.map((p) => (
                        <button
                          key={p.tenancy.tenancy_id}
                          onClick={() => setSelectedProperty(p)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            selectedProperty?.tenancy.tenancy_id === p.tenancy.tenancy_id
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-50 hover:bg-slate-100"
                          }`}
                        >
                          <p className="font-medium truncate">{p.tenancy.property_address || p.tenancy.address}</p>
                          {p.active_maintenance > 0 && (
                            <Badge className="mt-1 bg-amber-500 text-white">
                              {p.active_maintenance} job{p.active_maintenance > 1 ? "s" : ""}
                            </Badge>
                          )}
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <div className="lg:col-span-3 space-y-6">
                  {selectedProperty && (
                    <>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-xl font-semibold">{selectedProperty.tenancy.property_address || selectedProperty.tenancy.address}</h2>
                          {selectedProperty.tenant && (
                            <p className="text-slate-500 flex items-center gap-2 mt-1">
                              <User className="w-4 h-4" />
                              Tenant: {selectedProperty.tenant.name}
                            </p>
                          )}
                          <p className="text-2xl font-bold mt-2">
                            {formatCurrency(selectedProperty.tenancy.rent_amount)}/month
                          </p>
                        </div>
                        <Badge className={
                          selectedProperty.tenancy.status === "active" ? "bg-green-100 text-green-700" :
                          "bg-slate-100 text-slate-700"
                        }>
                          {selectedProperty.tenancy.status?.toUpperCase() || "ACTIVE"}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Tabs defaultValue="todo" className="space-y-4">
                    <TabsList>
                      <TabsTrigger value="todo" data-testid="tab-todo">
                        <FileCheck className="w-4 h-4 mr-2" />
                        To-Do List
                      </TabsTrigger>
                      <TabsTrigger value="rent">
                        <PoundSterling className="w-4 h-4 mr-2" />
                        Rent
                      </TabsTrigger>
                      <TabsTrigger value="maintenance">
                        <Wrench className="w-4 h-4 mr-2" />
                        Maintenance
                        {selectedProperty.active_maintenance > 0 && (
                          <Badge className="ml-1 bg-amber-500 text-white">{selectedProperty.active_maintenance}</Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="compliance">
                        <Shield className="w-4 h-4 mr-2" />
                        Compliance
                      </TabsTrigger>
                      <TabsTrigger value="feedback" data-testid="tab-feedback">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Feedback
                      </TabsTrigger>
                    </TabsList>

                    {/* To-Do List Tab */}
                    <TabsContent value="todo">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <FileCheck className="w-5 h-5 text-emerald-600" />
                            Your To-Do List
                          </CardTitle>
                          <CardDescription>Complete these items to get your property ready for tenants</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* ID Upload */}
                          <div className={`flex items-center gap-4 p-4 rounded-lg border ${
                            uploadedDocs.photo_id ? "bg-green-50 border-green-200" : "bg-white"
                          }`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              uploadedDocs.photo_id ? "bg-green-500 text-white" : "bg-slate-100 text-slate-400"
                            }`}>
                              {uploadedDocs.photo_id ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${uploadedDocs.photo_id ? "text-green-700" : "text-slate-800"}`}>
                                Upload Photo ID
                              </p>
                              <p className="text-sm text-slate-500">
                                {uploadedDocs.photo_id ? `Uploaded: ${uploadedDocs.photo_id.filename || "ID Document"}` : "Passport or driving licence"}
                              </p>
                            </div>
                            {!uploadedDocs.photo_id ? (
                              <Button size="sm" variant="outline" onClick={() => openUploadModal("photo_id", "Photo ID")} data-testid="upload-photo-id-btn">
                                <Upload className="w-4 h-4 mr-1" /> Upload
                              </Button>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                            )}
                          </div>

                          {/* Proof of Ownership */}
                          <div className={`flex items-center gap-4 p-4 rounded-lg border ${
                            uploadedDocs.proof_of_ownership ? "bg-green-50 border-green-200" : "bg-white"
                          }`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              uploadedDocs.proof_of_ownership ? "bg-green-500 text-white" : "bg-slate-100 text-slate-400"
                            }`}>
                              {uploadedDocs.proof_of_ownership ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${uploadedDocs.proof_of_ownership ? "text-green-700" : "text-slate-800"}`}>
                                Proof of Ownership
                              </p>
                              <p className="text-sm text-slate-500">
                                {uploadedDocs.proof_of_ownership ? `Uploaded: ${uploadedDocs.proof_of_ownership.filename || "Ownership Document"}` : "Land registry or mortgage statement"}
                              </p>
                            </div>
                            {!uploadedDocs.proof_of_ownership ? (
                              <Button size="sm" variant="outline" onClick={() => openUploadModal("proof_of_ownership", "Proof of Ownership")} data-testid="upload-ownership-btn">
                                <Upload className="w-4 h-4 mr-1" /> Upload
                              </Button>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                            )}
                          </div>

                          {/* Bank Details */}
                          <div className={`flex items-center gap-4 p-4 rounded-lg border ${
                            selectedProperty.tenancy.landlord_bank_details ? "bg-green-50 border-green-200" : "bg-white"
                          }`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              selectedProperty.tenancy.landlord_bank_details ? "bg-green-500 text-white" : "bg-slate-100 text-slate-400"
                            }`}>
                              {selectedProperty.tenancy.landlord_bank_details ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${selectedProperty.tenancy.landlord_bank_details ? "text-green-700" : "text-slate-800"}`}>
                                Bank Details for Rent
                              </p>
                              <p className="text-sm text-slate-500">Account for rent payments</p>
                            </div>
                            {!selectedProperty.tenancy.landlord_bank_details && (
                              <Button size="sm" variant="outline">
                                Add Details
                              </Button>
                            )}
                          </div>

                          {/* Management Agreement */}
                          <div className={`flex items-center gap-4 p-4 rounded-lg border ${
                            selectedProperty.tenancy.agreement_signed ? "bg-green-50 border-green-200" : "bg-white"
                          }`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              selectedProperty.tenancy.agreement_signed ? "bg-green-500 text-white" : "bg-slate-100 text-slate-400"
                            }`}>
                              {selectedProperty.tenancy.agreement_signed ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${selectedProperty.tenancy.agreement_signed ? "text-green-700" : "text-slate-800"}`}>
                                Sign Management Agreement
                              </p>
                              <p className="text-sm text-slate-500">Property management contract</p>
                            </div>
                            {!selectedProperty.tenancy.agreement_signed && (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                                <Pen className="w-4 h-4 mr-1" /> Sign
                              </Button>
                            )}
                          </div>

                          {/* Compliance Items */}
                          {selectedProperty.compliance?.map((item, i) => {
                            const status = getComplianceStatus(item);
                            return (
                              <div key={i} className={`flex items-center gap-4 p-4 rounded-lg border ${
                                status === "valid" ? "bg-green-50 border-green-200" : 
                                status === "expiring" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"
                              }`}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  status === "valid" ? "bg-green-500 text-white" : 
                                  status === "expiring" ? "bg-amber-500 text-white" : "bg-red-500 text-white"
                                }`}>
                                  {status === "valid" ? <CheckCircle className="w-5 h-5" /> : 
                                   status === "expiring" ? <Clock className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-medium ${
                                    status === "valid" ? "text-green-700" : 
                                    status === "expiring" ? "text-amber-700" : "text-red-700"
                                  }`}>
                                    {item.type || item.name}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    {status === "valid" ? `Valid until ${new Date(item.expiry_date).toLocaleDateString()}` :
                                     status === "expiring" ? `Expires ${new Date(item.expiry_date).toLocaleDateString()}` :
                                     "Expired - renewal required"}
                                  </p>
                                </div>
                                {status !== "valid" && (
                                  <Button size="sm" variant="outline">
                                    <Upload className="w-4 h-4 mr-1" /> Upload
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="rent">
                      <Card>
                        <CardHeader>
                          <CardTitle>Recent Rent Payments</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {selectedProperty.recent_rent?.length === 0 ? (
                            <p className="text-slate-500 text-center py-4">No rent records</p>
                          ) : (
                            <div className="space-y-3">
                              {selectedProperty.recent_rent?.map((r, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                  <div>
                                    <p className="font-medium">{formatCurrency(r.amount)}</p>
                                    <p className="text-sm text-slate-500">
                                      Due: {new Date(r.due_date).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <Badge className={
                                    r.status === "paid" ? "bg-green-100 text-green-700" :
                                    r.status === "overdue" ? "bg-red-100 text-red-700" :
                                    "bg-amber-100 text-amber-700"
                                  }>
                                    {r.status?.toUpperCase()}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="maintenance">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Wrench className="w-5 h-5" />
                            Maintenance Jobs
                          </CardTitle>
                          <CardDescription>
                            {selectedProperty.active_maintenance > 0 && (
                              <span className="text-amber-600 font-medium">
                                {selectedProperty.active_maintenance} active job{selectedProperty.active_maintenance > 1 ? 's' : ''}
                              </span>
                            )}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {selectedProperty.maintenance_jobs?.length === 0 ? (
                            <p className="text-slate-500 text-center py-4">No maintenance jobs</p>
                          ) : (
                            <div className="space-y-4">
                              {selectedProperty.maintenance_jobs?.map((job) => (
                                <div key={job.job_id} className="p-4 border rounded-lg bg-white shadow-sm">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className="font-semibold text-lg">{job.title}</p>
                                        {job.priority === "high" && (
                                          <Badge className="bg-red-100 text-red-700">Urgent</Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-600">{job.description}</p>
                                      <p className="text-xs text-slate-400 mt-2">
                                        Reported: {new Date(job.created_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <Badge className={
                                      job.status === "completed" ? "bg-green-100 text-green-700" :
                                      job.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                      job.status === "quote_pending" ? "bg-orange-100 text-orange-700" :
                                      job.status === "scheduled" ? "bg-indigo-100 text-indigo-700" :
                                      "bg-amber-100 text-amber-700"
                                    }>
                                      {job.status?.replace(/_/g, " ").toUpperCase()}
                                    </Badge>
                                  </div>
                                  
                                  {/* Contractor Info */}
                                  {job.contractor_name && (
                                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-3 bg-slate-50 p-2 rounded">
                                      <HardHat className="w-4 h-4" />
                                      <span>Contractor: <strong>{job.contractor_name}</strong></span>
                                    </div>
                                  )}
                                  
                                  {/* Quote Info - Show if quote exists */}
                                  {job.quote_amount && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-amber-800 flex items-center gap-2">
                                          <FileCheck className="w-4 h-4" />
                                          Quote Received
                                        </span>
                                        <span className="text-xl font-bold text-amber-900">
                                          £{job.quote_amount.toFixed(2)}
                                        </span>
                                      </div>
                                      {job.quote_description && (
                                        <p className="text-sm text-amber-700 mb-3">
                                          "{job.quote_description}"
                                        </p>
                                      )}
                                      
                                      {/* Action buttons for pending quotes */}
                                      {job.status === "quote_pending" && (
                                        <div className="flex gap-2 mt-3">
                                          <Button 
                                            size="sm" 
                                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                            onClick={() => handleViewQuote(job)}
                                            data-testid={`view-quote-${job.job_id}`}
                                          >
                                            <Eye className="w-4 h-4 mr-2" />
                                            View & Approve
                                          </Button>
                                        </div>
                                      )}
                                      
                                      {/* Show approval status */}
                                      {job.status === "accepted" && (
                                        <div className="flex items-center gap-2 text-green-700 mt-2">
                                          <CheckCircle className="w-4 h-4" />
                                          <span className="text-sm font-medium">Quote Approved - Work Scheduled</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="compliance">
                      <Card>
                        <CardHeader>
                          <CardTitle>Compliance Documents</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {selectedProperty.compliance?.length === 0 ? (
                            <p className="text-slate-500 text-center py-4">No compliance records</p>
                          ) : (
                            <div className="space-y-3">
                              {selectedProperty.compliance?.map((item, i) => {
                                const status = getComplianceStatus(item);
                                return (
                                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      {status === "valid" ? (
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                      ) : status === "expiring" ? (
                                        <Clock className="w-5 h-5 text-amber-500" />
                                      ) : (
                                        <AlertTriangle className="w-5 h-5 text-red-500" />
                                      )}
                                      <div>
                                        <p className="font-medium">{item.type || item.name}</p>
                                        {item.expiry_date && (
                                          <p className="text-sm text-slate-500">
                                            Expires: {new Date(item.expiry_date).toLocaleDateString()}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <Badge className={
                                      status === "valid" ? "bg-green-100 text-green-700" :
                                      status === "expiring" ? "bg-amber-100 text-amber-700" :
                                      "bg-red-100 text-red-700"
                                    }>
                                      {status.toUpperCase()}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Feedback Tab */}
                    <TabsContent value="feedback">
                      <FeedbackList />
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Quote Detail Modal */}
      <Dialog open={quoteModal} onOpenChange={setQuoteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-amber-600" />
              Quote for Approval
            </DialogTitle>
            <DialogDescription>
              Review the contractor's quote and choose how to proceed
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-4 py-4">
              {/* Issue Details */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">{selectedJob.title}</h4>
                <p className="text-sm text-slate-600">{selectedJob.description}</p>
                <p className="text-xs text-slate-400 mt-2">
                  Property: {selectedJob.property_address}
                </p>
              </div>

              {/* Contractor */}
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <HardHat className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{selectedJob.contractor_name}</p>
                  <p className="text-sm text-slate-500">Contractor</p>
                </div>
              </div>

              {/* Quote Amount */}
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-800 font-medium">Total Quote</span>
                  <span className="text-2xl font-bold text-amber-900">
                    £{selectedJob.quote_amount?.toFixed(2)}
                  </span>
                </div>
                {selectedJob.quote_description && (
                  <p className="text-sm text-amber-700 italic">
                    "{selectedJob.quote_description}"
                  </p>
                )}
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-700">How would you like to proceed?</h4>
                
                {/* Option 1: Pay Now */}
                <button
                  onClick={() => handleQuotePaymentRoute("paying_instance")}
                  disabled={actionLoading}
                  className="w-full p-4 border-2 border-emerald-200 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-left"
                  data-testid="pay-now-option"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-800">Pay Now</p>
                      <p className="text-sm text-slate-500">Pay instantly via secure card payment</p>
                    </div>
                    <span className="text-lg font-bold text-emerald-700">£{selectedJob.quote_amount?.toFixed(2)}</span>
                  </div>
                </button>

                {/* Option 2: Deduct from Rent (only if management enabled) */}
                {selectedJob.management_service_enabled && (
                  <button
                    onClick={() => handleQuotePaymentRoute("deducting_off_next_months_rent")}
                    disabled={actionLoading}
                    className="w-full p-4 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                    data-testid="deduct-rent-option"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-blue-800">Deduct from Next Month's Rent</p>
                        <p className="text-sm text-slate-500">Will be deducted from your rent statement</p>
                        <p className="text-xs text-blue-600 mt-1">Includes 10% management fee</p>
                      </div>
                      <span className="text-lg font-bold text-blue-700">£{(selectedJob.quote_amount * 1.1)?.toFixed(2)}</span>
                    </div>
                  </button>
                )}

                {/* Option 3: Handle Privately */}
                <button
                  onClick={() => handleQuotePaymentRoute("rejected_landlord_sorting_own")}
                  disabled={actionLoading}
                  className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-colors text-left"
                  data-testid="handle-privately-option"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-700">I'll Handle This Myself</p>
                      <p className="text-sm text-slate-500">Decline quote and arrange repairs privately</p>
                    </div>
                  </div>
                </button>
              </div>

              {actionLoading && (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-slate-500">Processing...</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setQuoteModal(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compliance Order Modal */}
      <Dialog open={complianceModal} onOpenChange={setComplianceModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              {selectedAlert?.compliance_name} Expiring
            </DialogTitle>
            <DialogDescription>
              Choose how you'd like to handle this renewal
            </DialogDescription>
          </DialogHeader>

          {selectedAlert && (
            <div className="space-y-4 py-4">
              {/* Property Details */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium">{selectedAlert.property_address}</p>
                <p className="text-sm text-amber-600 mt-1">
                  Expires: {selectedAlert.expiry_date} ({selectedAlert.days_until_expiry} days remaining)
                </p>
              </div>

              {/* Option 1: Order through agency */}
              <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50">
                <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Option 1: Order Through Us
                </h4>
                <p className="text-sm text-emerald-700 mb-3">
                  We'll arrange everything. Our contractor will contact the tenant to book a convenient time.
                </p>
                <div className="bg-white p-3 rounded border border-emerald-200 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-800">Total Price (inc VAT)</span>
                    <span className="text-xl font-bold text-emerald-900">
                      £{selectedAlert.price_inc_vat?.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleComplianceAction(selectedAlert.alert_id, "pay_now")}
                    disabled={actionLoading}
                    data-testid="compliance-pay-now"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PoundSterling className="w-4 h-4 mr-2" />}
                    Pay Now
                  </Button>
                  {selectedAlert.can_deduct_from_rent && (
                    <Button
                      variant="outline"
                      className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => handleComplianceAction(selectedAlert.alert_id, "deduct_from_rent")}
                      disabled={actionLoading}
                      data-testid="compliance-deduct-rent"
                    >
                      Deduct from Next Month's Rent
                    </Button>
                  )}
                  {!selectedAlert.can_deduct_from_rent && (
                    <p className="text-xs text-slate-500 text-center">
                      Rent deduction only available for fully managed properties with active tenancies
                    </p>
                  )}
                </div>
              </div>

              {/* Option 2: Arrange own */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-slate-700 mb-2">Option 2: I'll Arrange My Own</h4>
                <p className="text-sm text-slate-500 mb-3">
                  If you've already booked or prefer to use your own contractor, let us know.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleComplianceAction(selectedAlert.alert_id, "decline")}
                  disabled={actionLoading}
                  data-testid="compliance-arrange-own"
                >
                  I'll Arrange My Own
                </Button>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Please send us a copy of the certificate once completed
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Upload Modal */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload {uploadItem?.label}</DialogTitle>
            <DialogDescription>
              Select a file to upload. Supported formats: PDF, JPG, PNG (max 10MB)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText className="w-10 h-10 mx-auto text-emerald-600" />
                  <p className="font-medium text-slate-800">{selectedFile.name}</p>
                  <p className="text-sm text-slate-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-10 h-10 mx-auto text-slate-400" />
                  <p className="text-slate-600">Click to select a file</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-input"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleFileUpload} 
              disabled={!selectedFile || uploading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandlordDashboard;
